import { getFileExtension, getFileId } from "@/utils/file"
import {
	AdjustmentsHistory,
	AdjustmentsHistoryRecord,
	PhotoMetadataRecord,
	PhotoRecord,
} from "./types"
import { HistoryRecordAction } from "@/lib/catalog/types"
import { PhotoId } from "@/store/undoable-state"
import { hashObject } from "@/utils/hash"
import { Adjustments } from "@/core/types"
import { getDefaultAdjustments } from "@/core/adjustments"
import { applyChange, diff, type Diff } from "deep-diff"

export const diffAdjustments = (a: Adjustments, b: Adjustments) => {
	return diff(a, b, (path: string[], key: string) => {
		if (key === "points" && path.some((p) => p === "strokes")) {
			// eslint-disable-next-line
			const aParent = path.reduce((obj, key) => obj[key], a)
			// eslint-disable-next-line
			const bParent = path.reduce((obj, key) => obj[key], b)
			// Return true to skip diffing if lengths match
			return "length" in aParent && "length" in bParent && aParent.length === bParent.length
				? true
				: false
		}
		return false
	})
}

/**
 * deep-diff omits `path` for root-level changes (e.g. an object diffed against
 * null) and `applyChange` then throws reading `path[0]`. Such deltas only come
 * from a corrupted record, so they are skipped instead of applied.
 */
export const isApplicableAdjustmentsDelta = (delta: Diff<Adjustments> | null | undefined) =>
	delta != null && Array.isArray(delta.path) && delta.path.length > 0

export const applyAdjustmentsDeltas = (
	adjustments: Adjustments,
	deltas: Diff<Adjustments>[],
): { skipped: Diff<Adjustments>[] } => {
	const skipped: Diff<Adjustments>[] = []
	for (const delta of deltas) {
		if (isApplicableAdjustmentsDelta(delta)) {
			applyChange(adjustments, undefined, delta)
		} else {
			skipped.push(delta)
		}
	}
	return { skipped }
}

export const isAdjustmentsObject = (value: unknown): value is Adjustments =>
	typeof value === "object" && value !== null && !Array.isArray(value)

export const getDefaultAdjustmentHistory = (photo: PhotoRecord): AdjustmentsHistory => {
	const defaultAdjustments = getDefaultAdjustments(photo)
	const originalState: AdjustmentsHistoryRecord = {
		photoId: photo.id,
		message: "Original",
		stepIndex: 0,
		adjustments: defaultAdjustments,
		adjustmentsDigest: hashObject(defaultAdjustments),
		action: HistoryRecordAction.Import,
	}

	return [originalState]
}

export const createPhotoRecord = (
	fileOrId: File | PhotoId,
	properties?: Partial<PhotoRecord>,
): PhotoRecord => {
	let id: string, fileName: string

	if (fileOrId instanceof File) {
		id = getFileId(fileOrId)
		fileName = fileOrId.name
	} else {
		id = fileOrId
		fileName = fileOrId.split("|")[0] ?? "filename"
	}

	return {
		id,
		fileName,
		parentId: null,
		rating: null,
		historyIndex: null,
		customSortingIndex: null,
		isAnchorImage: false,
		isHidden: false,
		isUnsupported: false,
		smartCullingTag: null,
		manualCullingTag: null,
		flag: null,
		kind: "default",
		status: "none",
		error: null,
		presetId: null,
		styleId: null,
		styleVersion: null,
		adjustmentsDigest: null,
		colorTag: null,
		currentTask: null,
		adjustedDate: null,
		publishedDates: {},
		publishedExportSettings: {},
		...properties,
	}
}

export const createBasicPhotoMetadata = (file: File, id?: string): PhotoMetadataRecord => {
	const photoId = id ?? getFileId(file)
	const extension = getFileExtension(file.name) ?? "unknown"
	return {
		isComplete: false,
		hasEmbeddedPreview: null,
		photoId: photoId,
		size: file.size,
		extension: extension,
		dateModified: file.lastModified,
		dateCreated: null,
		dateCreatedOffset: null,
		width: null,
		height: null,
		orientation: null,
		iso: null,
		focalLength: null,
		focalLength35mm: null,
		focusDistance: null,
		shutterSpeed: null,
		fNumber: null,
		lensMake: null,
		lensModel: null,
		cameraMake: null,
		cameraModel: null,
		cameraSerialNumber: null,
		flash: null,
		rating: null,
		artist: null,
		colorTag: null,
		flag: null,
		whiteLevel: null,
		blackLevel: null,
		auxiliaryData: null,
		copyright: null,
	}
}

export const createVirtualPhotoRecord = (
	photo: PhotoRecord,
	customSortingIndex?: number,
): PhotoRecord => {
	const shouldCopyAdjustments = photo.kind === "locked" || photo.kind === "reference"

	return {
		id: photo.id + "|" + new Date().getTime(),
		parentId: photo.id,
		fileName: photo.fileName,
		rating: photo.rating,
		historyIndex: shouldCopyAdjustments ? photo.historyIndex : null,
		adjustmentsDigest: shouldCopyAdjustments ? photo.adjustmentsDigest : null,
		customSortingIndex: customSortingIndex ?? photo.customSortingIndex,
		isAnchorImage: false,
		isHidden: false,
		isUnsupported: false,
		smartCullingTag: photo.smartCullingTag === "Reject" ? "Reject" : "Candidate",
		manualCullingTag: null,
		flag: "unflagged",
		kind: shouldCopyAdjustments ? "locked" : "default",
		status: "none",
		error: null,
		presetId: null,
		styleId: null,
		colorTag: photo.colorTag,
		styleVersion: null,
		currentTask: null,
		adjustedDate: photo.adjustedDate,
		publishedDates: {},
		publishedExportSettings: {},
	}
}

export const getUpdatedPhotoRecord = (
	history: AdjustmentsHistoryRecord[],
	index: number,
): Partial<PhotoRecord> => {
	const styleApplied = getProfileApplied(history)
	const presetApplied = getLatestPresetApplied(history)

	const updatedPhoto: Partial<PhotoRecord> = {
		historyIndex: index,
		presetId: presetApplied ?? null,
		styleId: styleApplied.styleId ?? null,
		styleVersion: styleApplied.styleVersion ?? null,
		adjustmentsDigest: history.at(index)?.adjustmentsDigest ?? null,
		adjustedDate: Date.now(),
	}

	return updatedPhoto
}

export const getLatestPresetApplied = (history: AdjustmentsHistoryRecord[]) => {
	const reversed = history.slice().reverse()
	for (const record of reversed) {
		switch (record.action) {
			case HistoryRecordAction.Reset:
				return null
			case HistoryRecordAction.AutoEnhance:
				if (record.presetId) return record.presetId
				else return null
			case HistoryRecordAction.PasteAdjustments:
			case HistoryRecordAction.ApplyAIStyle:
				return null
			case HistoryRecordAction.ApplyPreset:
				return record.presetId
			case HistoryRecordAction.ManuallyAdjust:
			case HistoryRecordAction.Import:
				continue
		}
	}
	return null
}

export const getProfileApplied = (history: AdjustmentsHistoryRecord[]) => {
	for (const record of history.slice().reverse()) {
		switch (record.action) {
			case HistoryRecordAction.AutoEnhance:
			case HistoryRecordAction.ApplyPreset:
			case HistoryRecordAction.PasteAdjustments:
			case HistoryRecordAction.Reset:
				return { styleId: null, styleVersion: null }
			case HistoryRecordAction.ApplyAIStyle:
				return { styleId: record.styleId, styleVersion: record.styleVersion }
			case HistoryRecordAction.ManuallyAdjust:
			case HistoryRecordAction.Import:
				continue
		}
	}
	return { styleId: null, styleVersion: null }
}

export interface AdjustmentsOptions {
	message: string
	changedValue?: string
	adjustments: Adjustments
	presetId?: string
	styleId?: string
	styleVersion?: number
	action: HistoryRecordAction
}

export const createHistoryRecord = (
	photoId: string,
	index: number,
	options: AdjustmentsOptions,
) => {
	const digest = hashObject(options.adjustments)

	const record: AdjustmentsHistoryRecord = {
		stepIndex: index,
		photoId: photoId,
		message: options.message,
		changedValue: options.changedValue,
		adjustments: options.adjustments,
		adjustmentsDigest: digest,
		presetId: options.presetId,
		styleId: options.styleId,
		styleVersion: options.styleVersion,
		action: options.action,
	}
	return record
}

export const getUpdatedHistoryWithAdjustments = (
	photo: PhotoRecord,
	history: AdjustmentsHistoryRecord[],
	options: AdjustmentsOptions,
) => {
	const currentIndex = photo.historyIndex ?? 0
	const currentHistory = history.slice(0, currentIndex + 1)
	const index = currentHistory.length

	const record = createHistoryRecord(photo.id, index, options)
	const newHistory = currentHistory.concat(record)
	return { newHistory, record }
}

export const getWhitelistedKeys = <T>(keys: { [key in keyof T]: boolean }) => {
	return Object.entries(keys)
		.filter(([_, value]) => !!value)
		.map(([key]) => key as keyof T)
}

/**
 * Check if metadata indicates no embedded preview exists.
 * Handles SQLite storing false as 0 (integer) instead of boolean.
 */
export const hasNoEmbeddedPreview = (metadata: PhotoMetadataRecord | undefined): boolean => {
	return metadata?.hasEmbeddedPreview === false || (metadata?.hasEmbeddedPreview as unknown) === 0
}
